import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ExternalLink, Eye, ShieldCheck } from "lucide-react";
import { useQuiz } from "../context/QuizContext";
import { Page, PageContainer } from "../components/ui/page";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/data-display";
import { ErrorState, LoadingState } from "../components/ui/feedback";
import { QuizProgress } from "../components/quiz/QuizUI";
import { cn } from "../lib/utils";

const MicrosoftLinkTest = () => {
  const navigate = useNavigate();
  const {
    setMicrosoftLinkAnswer,
    setFinalTestId,
    finalChallenge,
    questionSet,
    emailSet,
    mcqAnswers,
    emailAnswers,
    setFlowStage,
    setAllowResults,
    resetQuiz,
  } = useQuiz();
  const [selectedLink, setSelectedLink] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const previousQuestionCount = (questionSet?.length || 0) + (emailSet?.length || 0);
  const questionNumber = previousQuestionCount + 1;
  const answeredCount = Object.keys(mcqAnswers || {}).length + Object.keys(emailAnswers || {}).length + (selectedLink ? 1 : 0);
  const indicators = Array.from({ length: questionNumber }, (_, index) => ({
    active: index === questionNumber - 1,
    complete: index < questionNumber - 1 || (index === questionNumber - 1 && Boolean(selectedLink)),
  }));

  useEffect(() => {
    if (finalChallenge) {
      const data = finalChallenge;
      setPrompt(data.prompt);
      setFinalTestId(data.id);
      setLinks([
        { id: "link1", displayUrl: data.link1_display, actualUrl: data.link1_actual },
        { id: "link2", displayUrl: data.link2_display, actualUrl: data.link2_actual },
      ]);
    } else setLoadError("The assessment session has expired. Start again from the learning page.");
    setLoading(false);
  }, [finalChallenge, setFinalTestId]);

  const submit = () => {
    if (!selectedLink) return;
    setMicrosoftLinkAnswer(selectedLink.actualUrl);
    setFlowStage("final_done");
    setAllowResults(true);
    navigate("/results", { state: { fromFinal: true } });
  };

  if (loading) return <Page><LoadingState title="Preparing the final challenge" description="Loading the link inspection exercise." /></Page>;
  if (loadError || links.length !== 2) return <Page><PageContainer size="sm"><Card><ErrorState title="Final challenge unavailable" description={loadError || "Two link options are required."} onRetry={() => { resetQuiz(); setFlowStage("authenticated"); navigate("/education"); }} /></Card></PageContainer></Page>;

  return (
    <Page>
      <PageContainer size="lg">
        <QuizProgress
          current={questionNumber}
          total={questionNumber}
          answered={answeredCount}
          indicators={indicators}
        />

        <div className="mb-4 mt-5 flex items-center justify-between gap-3">
          <Badge variant="primary">Section 3 of 3</Badge>
          <span className="text-sm font-medium text-muted-foreground">Final question</span>
        </div>

        <Card>
          <CardContent className="p-5 sm:p-7">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-subtle text-primary">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Final link check</p>
                <h1 className="mt-1 text-xl font-semibold leading-7 text-foreground sm:text-2xl">Inspect the destination</h1>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{prompt}</p>
              </div>
            </div>

            <div className="mt-6 overflow-visible rounded-lg border border-border bg-surface shadow-sm">
              <div className="flex flex-col gap-4 rounded-t-lg border-b border-border bg-surface-muted/70 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src="/i2e.ico"
                    alt="I2E Consulting"
                    className="h-10 w-10 rounded-md border border-border bg-white object-contain"
                    onError={event => { event.currentTarget.style.display = "none"; }}
                  />
                  <div>
                    <p className="font-semibold text-foreground">I2E Consulting</p>
                    <p className="text-xs text-muted-foreground">Microsoft work account sign-in</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-md bg-primary-subtle px-3 py-2 text-xs font-medium text-primary-hover">
                  <Eye className="h-4 w-4" />
                  Hover or focus to reveal each destination
                </div>
              </div>

              <fieldset className="p-5">
                <legend className="sr-only">Select the safest link to continue</legend>
                <p className="mx-auto mb-4 max-w-2xl text-sm font-medium text-foreground">Which link is safest to open?</p>
                <div className="mx-auto grid max-w-2xl gap-3">
                  {links.map((link, index) => {
                    const selected = selectedLink?.id === link.id;
                    return (
                      <button
                        key={link.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setSelectedLink(link)}
                        className={cn(
                          "group relative flex w-full items-center gap-4 rounded-lg border px-4 py-3 text-left transition-all focus:z-30 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 hover:z-30",
                          selected
                            ? "border-primary bg-primary-subtle shadow-sm"
                            : "border-border bg-surface hover:border-primary/40 hover:bg-surface-muted"
                        )}
                      >
                        <span className={cn(
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2",
                          selected ? "border-primary bg-primary" : "border-muted-foreground/70 bg-surface"
                        )}>
                          {selected && <span className="h-2 w-2 rounded-full bg-white" />}
                        </span>
                        <Badge variant={selected ? "primary" : "neutral"}>Option {index + 1}</Badge>
                        <span className="flex min-w-0 flex-1 items-start gap-2 font-mono text-sm font-semibold leading-6 text-primary">
                          <ExternalLink className="mt-1 h-4 w-4 shrink-0" />
                          <span className="break-all">{link.displayUrl}</span>
                        </span>
                        <span className="pointer-events-none absolute left-10 right-4 top-full z-50 mt-2 block overflow-x-auto whitespace-nowrap rounded-md bg-slate-950 px-3 py-2 font-mono text-xs leading-5 text-amber-300 opacity-0 shadow-float transition-opacity group-hover:opacity-100 group-focus:opacity-100">
                          <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">Actual destination</span>
                          <span>{link.actualUrl}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="mx-auto mt-4 max-w-2xl text-xs leading-5 text-muted-foreground">
                  Displayed link text can be misleading. Compare the actual domain before making your choice.
                </p>
              </fieldset>
            </div>

            <div className="mt-6 flex justify-center">
              <Button size="lg" className="w-full sm:w-auto" disabled={!selectedLink} onClick={submit}>
                Submit final answer
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    </Page>
  );
};

export default MicrosoftLinkTest;
